import * as Sentry from "@sentry/react";
import { setMeasurement } from "@sentry/core";
import { useCallback } from "react";
import { currentUserRoles, restrictAccessToDrivingSchoolIds } from "./backoffice.access_control";

let _sendStartupTrace = true;
let _mainStartedMark: PerformanceMark | undefined;
let _loadingInitialDataMark: PerformanceMark | undefined;
let _initialDataLoadedMark: PerformanceMark | undefined;
let _renderingUiMark: PerformanceMark | undefined;
let _uiRenderedMark: PerformanceMark | undefined;

export function doNotSendStartupTrace() {
  _sendStartupTrace = false;
}

export function markMainStarted() {
  _mainStartedMark = performance.mark("main() ...");
  const dt = _mainStartedMark.startTime - autovio.indexHtmlMark.startTime;
  console.info(`Initial Javascript loaded in ${Math.round(dt)} ms`);
}

export function markLoadingData(): void {
  if (_loadingInitialDataMark) return; // only record the first time
  _loadingInitialDataMark = performance.mark("loading initial data ...");
}

export function markDataLoaded(t: DOMHighResTimeStamp): void {
  if (_initialDataLoadedMark) return; // only record the first time
  _initialDataLoadedMark = performance.mark("initial data loaded", { startTime: t });
  const loadInitialDataDuration = Math.round(_initialDataLoadedMark.startTime - _loadingInitialDataMark!.startTime);
  console.info(`Initial data loaded in ${loadInitialDataDuration} ms`);
  Sentry.metrics.distribution("startup.load_initial_data", loadInitialDataDuration, {
    unit: "ms",
    attributes: {
      user_group: _getUserGroup(),
      path: location.pathname,
    },
  });
  if (_uiRenderedMark && _sendStartupTrace) {
    _reportStartupTrace();
  }
}

export function markRenderingUi() {
  const loadInitailJavascriptDuration = Math.round(_mainStartedMark!.startTime - autovio.indexHtmlMark.startTime);
  Sentry.metrics.distribution("startup.load_initial_javascript", loadInitailJavascriptDuration, { unit: "ms" });
  _renderingUiMark = performance.mark("rendering UI ...");
  const initializationDuration = Math.round(_renderingUiMark.startTime - _mainStartedMark!.startTime);
  console.info(`Initialization took ${initializationDuration} ms`);
  Sentry.metrics.distribution("startup.initialization", initializationDuration, { unit: "ms" });
}

export function useMarkUiRenderedRef<T extends Element>(): (element: T | null) => void {
  return useCallback((element: T | null) => {
    if (element) {
      markUiRendered();
    }
  }, []);
}

function markUiRendered(): void {
  if (_uiRenderedMark) return; // only record the first time
  _uiRenderedMark = performance.mark("UI rendered");
  const renderUiDuration = Math.round(_uiRenderedMark.startTime - _renderingUiMark!.startTime);
  console.info(`UI rendered in ${renderUiDuration} ms`);
  Sentry.metrics.distribution("startup.render_UI", renderUiDuration, {
    unit: "ms",
    attributes: {
      user_group: _getUserGroup(),
      path: location.pathname,
    },
  });
  if (_initialDataLoadedMark && _sendStartupTrace) {
    _reportStartupTrace();
  }
}

function _reportStartupTrace(): void {
  const { indexHtmlMark } = autovio;
  const loadInitialJavascriptDuration = Math.round(_mainStartedMark!.startTime - indexHtmlMark.startTime);
  const initializationDuration = Math.round(_renderingUiMark!.startTime - _mainStartedMark!.startTime);
  const loadInitialDataDuration = Math.round(_initialDataLoadedMark!.startTime - _loadingInitialDataMark!.startTime);
  const renderUiDuration = Math.round(_uiRenderedMark!.startTime - _renderingUiMark!.startTime);
  const endMark =
    _initialDataLoadedMark!.startTime > _uiRenderedMark!.startTime ? _initialDataLoadedMark! : _uiRenderedMark!;
  const totalStartupDuration = Math.round(endMark.startTime - indexHtmlMark.startTime);
  console.info(`Startup took ${totalStartupDuration} ms`);
  Sentry.metrics.distribution("startup.total_time", totalStartupDuration, {
    unit: "ms",
    attributes: {
      user_group: _getUserGroup(),
      path: location.pathname,
    },
  });
  Sentry.startSpan(
    {
      name: "backoffice startup",
      op: "startup",
      startTime: _absoluteSeconds(indexHtmlMark),
    },
    (rootSpan) => {
      setMeasurement("startup.load_initial_javascript_ms", loadInitialJavascriptDuration, "millisecond", rootSpan);
      setMeasurement("startup.initialization_ms", initializationDuration, "millisecond", rootSpan);
      setMeasurement("startup.load_initial_data_ms", loadInitialDataDuration, "millisecond", rootSpan);
      setMeasurement("startup.render_UI_ms", renderUiDuration, "millisecond", rootSpan);
      setMeasurement("startup.total_duration_ms", totalStartupDuration, "millisecond", rootSpan);
      Sentry.startSpan(
        {
          name: "load Initial Javascript",
          op: "startup.load_initial_javascript",
          startTime: _absoluteSeconds(indexHtmlMark),
        },
        (span) => span.end(_absoluteSeconds(_mainStartedMark!)),
      );
      Sentry.startSpan(
        {
          name: "Initialization",
          op: "startup.initialization",
          startTime: _absoluteSeconds(_mainStartedMark!),
        },
        (span) => span.end(_absoluteSeconds(_renderingUiMark!)),
      );
      Sentry.startSpan(
        {
          name: "render UI",
          op: "startup.render_UI",
          startTime: _absoluteSeconds(_renderingUiMark!),
        },
        (span) => span.end(_absoluteSeconds(_uiRenderedMark!)),
      );
      Sentry.startSpan(
        {
          name: "load initial data",
          op: "startup.load_initial_data",
          startTime: _absoluteSeconds(_loadingInitialDataMark!),
        },
        (span) => span.end(_absoluteSeconds(_initialDataLoadedMark!)),
      );
      rootSpan.end(_absoluteSeconds(endMark));
    },
  );
}

function _absoluteSeconds(performanceMark: PerformanceMark): number {
  return (performance.timeOrigin + performanceMark.startTime) / 1000;
}

function _getUserGroup() {
  if (currentUserRoles.includes("admin")) {
    return "admin";
  } else if (!restrictAccessToDrivingSchoolIds || restrictAccessToDrivingSchoolIds.length > 1) {
    return "intern";
  } else {
    return "extern";
  }
}
